import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React from 'react';
import {
  Box,
  Divider,
  Grid,
  KeyValue,
  MUIDataTable,
  MUIDataTableColumnDef,
  Typography,
} from 'web-library';
import { TFunction, useTranslation } from 'react-i18next';
import FormHeader from '../../../components/form-items/FormHeader';
import CustomFeeRow from '../../../components/CustomFeeRow';
import { ExamReviewResponse, ViewSelectedSubjectPapers } from '../../../api/candidate/exam/model';

import { typographyTheme } from 'web-library/theme/helper/tssHelper';
const useStyles = makeStyles()((theme) => ({
  container: {
    paddingBottom: theme.spacing(2),
  },
  payableFeesCell: {
    fontWeight: 'bold',
    color: theme.color.text.textInactive,
  },
  feeHeaderLabel: {
    color: theme.color.text.textHeader,
    fontWeight: 'bold',
    fontSize: '1.3em',
  },
  feeHeaderValue: {
    color: theme.color.text.textHeader,
    fontWeight: 'bold',
    fontSize: '1.1em',
  },
  payableFeesSubtotalValue: {
    fontWeight: 'bold',
  },
  payableFeesTotalValue: {
    fontWeight: 'bold',
    fontSize: '1.3em',
  },
  payableFeesSubtotalContainer: {
    paddingBottom: theme.spacing(4),
  },
  payableFeesContainer: {
    paddingBottom: theme.spacing(1),
  },
  dividerContainer: {
    paddingTop: theme.spacing(4),
  },
  subject: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 auto',
  },
  subjectName: {
    ...typographyTheme(theme, 'body2'),
    paddingBottom: theme.spacing(1),
  },
  optionalPaper: {
    ...typographyTheme(theme, 'subtitle2'),
    color: theme.color.text.textSecondary,
  },
  feeCell: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    whiteSpace: 'nowrap',
  },
  actionTaken: {
    color: theme.color.text.textTertiary,
  },
}));

const addComma = (index: number, length: number): string => (length - 1 !== index ? ', ' : '');

const renderOptionalPaper = (optionalPapers: ViewSelectedSubjectPapers[], t: TFunction<string>) =>
  optionalPapers.map((paper, index, { length }) => {
    if (paper.selected) {
      return `${t('exam->>ReviewExam.Paper')} ${paper.paperNo}${addComma(index, length)}`;
    }
    return '';
  });

const getTableColumn = (
  t: any,
  classes: any,
  selectedSubjects: any,
  gstPercentage: string | null
) => {
  const columns: MUIDataTableColumnDef[] = [
    {
      label: t('amendment->>AmendedSubjectExamFees.Subject Name'),
      name: 'subjectName',
      options: {
        setCellProps: () => ({ style: { width: '15%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Box className={classes.subject}>
            <Typography className={classes.subjectName}>
              {selectedSubjects[dataIndex].subjectName}
              {'\u00A0'}({selectedSubjects[dataIndex].subjectCode})
            </Typography>

            {selectedSubjects[dataIndex].optionalPapers.length > 0 && (
              <Typography className={classes.optionalPaper}>
                + {t('exam->>ReviewExam.Optional Papers')}:{'\u00A0'}
                {renderOptionalPaper(selectedSubjects[dataIndex].optionalPapers, t)}
              </Typography>
            )}
          </Box>
        ),
      },
    },

    {
      label: t('amendment->>AmendedSubjectExamFees.Subject Level'),
      name: 'subjectLevel',
      options: {
        setCellProps: () => ({ style: { width: '17%' } }),
        filter: false,
        customBodyRenderLite: (dataIndex: number, rowIndex: number) => (
          <Typography id={`text-subjectLevel-row${rowIndex}`} variant="body1">
            {selectedSubjects[dataIndex].subjectLevel}
          </Typography>
        ),
      },
    },
    {
      label: t('amendment->>AmendedSubjectExamFees.Exam Series'),
      name: 'examSeries',
      options: {
        setCellProps: () => ({ style: { width: '12%' } }),
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body1">{selectedSubjects[dataIndex].examSeries}</Typography>
        ),
      },
    },
    {
      label: t('amendment->>AmendedSubjectExamFees.Exam Level'),
      name: 'examLevel',
      options: {
        filter: false,
        setCellProps: () => ({ style: { width: '18%' } }),
        customBodyRenderLite: (dataIndex: number, rowIndex: number) => (
          <Typography id={`text-examLevel-row${rowIndex}`} variant="body1">
            {selectedSubjects[dataIndex].examLevel || 'N/A'}
          </Typography>
        ),
      },
    },
    {
      label: t('amendment->>AmendedSubjectExamFees.Language Medium'),
      name: 'language',
      options: {
        setCellProps: () => ({ style: { width: '12%' } }),
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body1">{selectedSubjects[dataIndex].language}</Typography>
        ),
      },
    },
    {
      name: 'fee',
      options: {
        setCellProps: () => ({ style: { width: '14%' } }),
        filter: false,
        customHeadLabelRender: () => (
          <Typography variant="h6" style={{ textAlign: 'right' }}>
            {t('exam->>ReviewExam.ExamFees')}
            <br />
            {gstPercentage ? t('exam->>ReviewExam.(incl GST) (SGD)', { gstPercentage }) : null}
          </Typography>
        ),
        customBodyRenderLite: (dataIndex: number, rowIndex: number) => (
          <Typography
            className={classes.feeCell}
            id={`text-examFees-row${rowIndex}`}
            variant="body1"
          >
            {selectedSubjects[dataIndex].fee.charAt(0) === '-'
              ? `-S$${selectedSubjects[dataIndex].fee.slice(1)}`
              : `S$${selectedSubjects[dataIndex].fee}`}
          </Typography>
        ),
      },
    },
    {
      label: t('amendment->>AmendedSubjectExamFees.Action Taken'),
      name: 'actionTaken',
      options: {
        setCellProps: () => ({ style: { width: '12%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="h6" className={classes.actionTaken}>
            {selectedSubjects[dataIndex].actionTaken}
          </Typography>
        ),
      },
    },
  ];
  return columns;
};

const showGstIndicator = (isIPsle?: number, gstIndicator?: string) => !isIPsle || gstIndicator;

const AmendedSubjectExamFees = (props: ExamReviewResponse) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const {
    selectedSubjects = [],
    examYear,
    payment,
    groupedLevelFees,
    gstRegistrationNumber,
  } = props;
  const isIPsle =
    groupedLevelFees?.length &&
    groupedLevelFees.filter((item) => item.examLevel === 'iPSLE').length;
  const gstPercentage = isIPsle && !payment?.gstIndicator ? null : payment?.gstPercentage || '7';
  const columns = getTableColumn(t, classes, selectedSubjects, gstPercentage);
  return (
    <Box className={classes.container}>
      <FormHeader
        title={t('amendment->>AmendedSubjectExamFees.Amended Subject(s) and Exam Fees')}
      />

      <KeyValue
        id="examYear"
        keyText={t('amendment->>AmendedSubjectExamFees.Exam Year')}
        valueText={examYear.toString()}
      />

      <Grid sm={12}>
        {selectedSubjects && <MUIDataTable columns={columns} data={selectedSubjects} />}
      </Grid>

      <Divider />

      {showGstIndicator(isIPsle, payment?.gstPercentage) && gstRegistrationNumber && (
        <KeyValue
          id="gstRegNo"
          keyText={t('amendment->>AmendedSubjectExamFees.GST Reg No.')}
          valueText={gstRegistrationNumber}
        />
      )}

      {groupedLevelFees.map((item) => (
        <>
          <CustomFeeRow
            variant="header"
            label={item.examLevel}
            value={t('amendment->>AmendedSubjectExamFees.Amount (SGD)')}
            currencyDenomination={false}
          />
          {item.adminFee && (
            <CustomFeeRow
              label={t('amendment->>AmendedSubjectExamFees.Admin Fee')}
              value={item.adminFee}
            />
          )}
          {item.basicFee && (
            <CustomFeeRow
              label={t('amendment->>AmendedSubjectExamFees.Basic Fee')}
              value={item.basicFee}
            />
          )}
          {item.lateEntryFee && (
            <CustomFeeRow
              label={t('amendment->>AmendedSubjectExamFees.Late Entry Fee')}
              value={item.lateEntryFee}
            />
          )}
          {item.amendmentFee && (
            <CustomFeeRow
              label={t('amendment->>AmendedSubjectExamFees.Amendment Fee')}
              value={item.amendmentFee}
            />
          )}
          {item.subjectAdminFee && (
            <CustomFeeRow
              label={t('amendment->>AmendedSubjectExamFees.Subject Admin Fee')}
              value={item.subjectAdminFee}
            />
          )}
          {item.totalSubjectFee && (
            <CustomFeeRow
              label={t('amendment->>AmendedSubjectExamFees.Total Subject Fee')}
              value={item.totalSubjectFee}
            />
          )}
          {item.subtotal && (
            <CustomFeeRow
              variant="subFooter"
              label={t('amendment->>AmendedSubjectExamFees.Subtotal')}
              value={item.subtotal}
            />
          )}
        </>
      ))}
      <Grid className={classes.dividerContainer} xs={12} sm={6}>
        <Divider />
      </Grid>
      <>
        {/* Total Charges */}
        <CustomFeeRow
          variant="header"
          label={t('amendment->>AmendedSubjectExamFees.Total Exam Fees')}
          value={t('amendment->>AmendedSubjectExamFees.Amount (SGD)')}
          currencyDenomination={false}
        />
        {showGstIndicator(isIPsle, payment.gstIndicator) && (
          <>
            <CustomFeeRow
              label={t('amendment->>AmendedSubjectExamFees.Goods and Services Tax (GST)')}
              value={payment.totalGst}
            />
          </>
        )}
        {payment.totalWithdrawalAmount && (
          <CustomFeeRow
            label={t('amendment->>AmendedSubjectExamFees.Total Withdrawal Amount')}
            value={payment.totalWithdrawalAmount}
          />
        )}
        {showGstIndicator(isIPsle, payment.gstIndicator) && (
          <CustomFeeRow
            label={t('amendment->>AmendedSubjectExamFees.TotalPayableFeeInclGST')}
            value={payment.totalFee}
          />
        )}

        <CustomFeeRow
          variant="footer"
          label={t('amendment->>AmendedSubjectExamFees.Total Payable')}
          value={payment.totalPayable || '0.00'}
        />
      </>
    </Box>
  );
};

export default AmendedSubjectExamFees;
